import { useCallback, useState } from "react";
import { UploadedFile } from "../types/UploadedFile.type";

interface UseFileUploadStateParams {
	multiple: boolean;
	onChange: (files: UploadedFile[] | null) => void;
	files: UploadedFile[];
}

export function useFileUploadState({
	multiple,
	onChange,
	files: initialFiles,
}: UseFileUploadStateParams) {
	// ensure files are never null
	const [files, setFiles] = useState<UploadedFile[]>(() => initialFiles ?? []);

	const addFiles = useCallback(
		(newFiles: UploadedFile[]) => {
			const newState = multiple
				? [...files, ...newFiles]
				: newFiles.slice(0, 1);
			setFiles(newState);
			onChange?.(newState);
		},
		[multiple, onChange, files]
	);

	const updateUploadedFile = useCallback(
		(uploadId: string, updatedFile: UploadedFile) => {
			const newState = files.map((file) => {
				if (file.uploadId === uploadId) {
					return { ...updatedFile };
				}
				return file;
			});

			setFiles(newState);
			onChange?.(newState);
		},
		[multiple, onChange, files]
	);

	const removeFile = useCallback(
		(id: number | string) => {
			const newState = files.filter((f) => {
				if (typeof id === "string") {
					return f.uploadId !== id;
				}
				return f.id !== id;
			});

			setFiles(newState);
			onChange?.(newState);
		},
		[multiple, onChange, files]
	);

	const clearFiles = () => setFiles([]);
	const replaceFiles = (files: UploadedFile[]) => setFiles(files);

	return {
		files: files,
		addFiles,
		clearFiles,
		removeFile,
		replaceFiles,
		updateUploadedFile,
	};
}
